import type { UsageProviderKind } from "@cmmd-forge/contracts";
import type { ProviderTotals } from "@cmmd-forge/shared/usageMerge";
import {
  formatCount,
  formatPercent,
  formatTokens,
  formatUsd,
} from "@cmmd-forge/shared/usageFormat";

import { cn } from "../../lib/utils";
import { PROVIDER_PRESENTATION } from "./usageProviders";
import type { UsageChartMetric } from "./UsageProviderChart";

/** Brand mark for the harness a row belongs to. */
export function ProviderMark({
  provider,
  className,
}: {
  readonly provider: UsageProviderKind;
  readonly className: string;
}) {
  const Mark = PROVIDER_PRESENTATION[provider].mark;
  return <Mark className={cn("shrink-0", className)} aria-hidden />;
}

/**
 * The financial answer, then the per-harness split.
 *
 * Reads in whatever units the metric toggle is showing, so the headline and the
 * chart series can never disagree about what is being measured. The subline
 * names the quantity — "API estimate" — rather than footnoting the number,
 * because the notice above the window already says this is not a bill.
 */
export function UsageSummaryPanel({
  metric,
  costUsd,
  totalTokens,
  sessions,
  providers,
  activeProviders,
}: {
  readonly metric: UsageChartMetric;
  readonly costUsd: number;
  readonly totalTokens: number;
  readonly sessions: number;
  readonly providers: readonly ProviderTotals[];
  readonly activeProviders: readonly UsageProviderKind[];
}) {
  return (
    <div className="flex min-w-0 flex-col gap-5">
      <div className="flex flex-col gap-1">
        <span className="text-4xl font-semibold text-foreground tabular-nums">
          {metric === "cost" ? formatUsd(costUsd) : formatTokens(totalTokens)}
        </span>
        <span className="text-xs text-muted-foreground">
          {metric === "cost"
            ? `${formatCount(sessions)} sessions · API estimate`
            : `${formatCount(sessions)} sessions`}
        </span>
      </div>

      {activeProviders.map((provider) => (
        <UsageProviderRow
          key={provider}
          metric={metric}
          provider={provider}
          totals={providers.find((entry) => entry.provider === provider) ?? NO_TOTALS}
        />
      ))}
    </div>
  );
}

function pluralSessions(sessions: number): string {
  return `${formatCount(sessions)} ${sessions === 1 ? "session" : "sessions"}`;
}

/** Zero totals for a lane the merge has no row for, so readers never branch. */
const NO_TOTALS = {
  costUsd: 0,
  totalTokens: 0,
  sessions: 0,
  costShare: 0,
  tokenShare: 0,
} as const;

function UsageProviderRow({
  metric,
  provider,
  totals = NO_TOTALS,
}: {
  readonly metric: UsageChartMetric;
  readonly provider: UsageProviderKind;
  readonly totals:
    | Pick<ProviderTotals, "costUsd" | "totalTokens" | "sessions" | "costShare" | "tokenShare">
    | undefined;
}) {
  const isCost = metric === "cost";
  const share = isCost ? totals.costShare : totals.tokenShare;
  const value = isCost ? formatUsd(totals.costUsd) : formatTokens(totals.totalTokens);
  const detail = isCost
    ? `${formatPercent(share)} of cost · ${formatTokens(totals.totalTokens)} tokens`
    : `${formatPercent(share)} of tokens · ${formatUsd(totals.costUsd)}`;
  const sessionLabel = pluralSessions(totals.sessions);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-4">
        <span className="flex min-w-0 items-center gap-2 text-sm text-foreground">
          {/* The dot carries the series colour, the mark carries the brand:
              two lanes can share a monochrome mark but never a series colour. */}
          <span
            aria-hidden
            className="size-2 shrink-0 rounded-full"
            style={{ backgroundColor: PROVIDER_PRESENTATION[provider].color }}
          />
          <ProviderMark provider={provider} className="size-4" />
          <span className="flex min-w-0 items-baseline gap-1.5">
            <span className="truncate">{PROVIDER_PRESENTATION[provider].label}</span>
            <span className="shrink-0 text-[11px] whitespace-nowrap text-muted-foreground tabular-nums">
              {sessionLabel}
            </span>
          </span>
        </span>
        <span className="shrink-0 text-sm font-medium text-foreground tabular-nums">{value}</span>
      </div>
      <span className="text-xs text-muted-foreground">{detail}</span>
    </div>
  );
}

/** Column labels for the token-mix strip, shared with the loading skeleton. */
export const USAGE_TOTALS_LABELS = [
  "Processed tokens",
  "Cached input",
  "Uncached input",
  "Output",
  "Cache savings",
] as const;

/** Where the processed tokens went, and what caching was worth. */
export function UsageTotals({
  merged,
}: {
  readonly merged: {
    readonly totalTokens: number;
    readonly cachedInputTokens: number;
    readonly uncachedInputTokens: number;
    readonly outputTokens: number;
    readonly costQuality: { readonly cacheSavingsUsd: number };
  };
}) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-sm font-medium text-foreground">Totals</h2>
      <div className="grid grid-cols-2 gap-x-6 gap-y-4 py-1 md:grid-cols-5">
        <UsageMetric label="Processed tokens" value={formatTokens(merged.totalTokens)} />
        <UsageMetric label="Cached input" value={formatTokens(merged.cachedInputTokens)} />
        <UsageMetric label="Uncached input" value={formatTokens(merged.uncachedInputTokens)} />
        <UsageMetric label="Output" value={formatTokens(merged.outputTokens)} />
        <UsageMetric label="Cache savings" value={formatUsd(merged.costQuality.cacheSavingsUsd)} />
      </div>
    </section>
  );
}

function UsageMetric({ label, value }: { readonly label: string; readonly value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-base font-medium text-foreground tabular-nums">{value}</span>
    </div>
  );
}
